import { useState, useEffect } from "react";
import { WelockLogo, WelockMark } from "@/components/WelockMark";
import { RobinhoodMark } from "@/components/RobinhoodMark";
import { DashboardPreview } from "@/components/DashboardPreview";
import { SCENES } from "@/components/AnimatedScenes";
import { useWallet } from "@/context/WalletContext";
import { useNavigate } from "react-router-dom";
import { getStats, fmtUsd, fmtNum } from "@/lib/api";
import {
  Lock, Layers, Clock, Waves, Send, Cpu, ArrowUpRight, ArrowRight, Loader2, Menu, X,
} from "lucide-react";

const PRODUCTS = [
  { id: "token-locker", icon: Lock, title: "Token Locker", desc: "Lock ERC-20 supply with flexible release schedules and verifiable proof of lock.", span: "lg:col-span-2", feature: true },
  { id: "lp-locker", icon: Layers, title: "LP Locker", desc: "Secure Uniswap V3 and V4 NFT positions directly on Robinhood Chain." },
  { id: "vesting", icon: Clock, title: "Vesting", desc: "Linear or cliff based distributions for teams and investors." },
  { id: "lp-vesting", icon: Waves, title: "LP Vesting", desc: "Automated liquidity unlock schedules synced to protocol milestones.", span: "lg:col-span-2", feature: true },
  { id: "multisender", icon: Send, title: "Multisender", desc: "Batch transfers to thousands of wallets in a single gas efficient run.", span: "lg:col-span-2", feature: true },
  { id: "agents", icon: Cpu, title: "Agents API", desc: "Programmatic locking, verification hooks and real time webhook alerts." },
];

const NAV_LINKS = [
  { href: "#products", label: "Products", id: "products" },
  { href: "#stats", label: "Metrics", id: "stats" },
  { href: "#network", label: "Network", id: "network" },
  { href: "/docs", label: "Docs", id: "docs" },
];

function Eyebrow({ children, className = "" }) {
  return <span className={`eyebrow ${className}`}>{children}</span>;
}

function Nav({ onLaunch, connecting }) {
  const { connected } = useWallet();
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed inset-x-0 top-0 z-40 px-4 pt-4 md:px-6">
      <div className="mx-auto max-w-7xl border border-welock-border bg-welock-surface/70 backdrop-blur-xl">
        <div className="flex items-center justify-between px-4 py-2.5">
          <WelockLogo />

          <nav className="hidden items-center border border-welock-border bg-welock-bg/60 md:flex" data-testid="nav-box">
            {NAV_LINKS.map((l, i) => (
              <a
                key={l.id}
                href={l.href}
                data-testid={`nav-${l.id}`}
                className={`px-5 py-2 font-mono text-[11px] uppercase tracking-widest text-welock-textSecondary transition-colors hover:bg-welock-surfaceHover hover:text-welock-textPrimary ${
                  i > 0 ? "border-l border-welock-border" : ""
                }`}
              >
                {l.label}
              </a>
            ))}
          </nav>

          <button
            data-testid="nav-launch-app"
            disabled={connecting}
            onClick={onLaunch}
            className="group hidden items-center gap-2 border border-welock-accent bg-welock-accent px-4 py-2 font-mono text-xs font-medium text-welock-bg transition-all hover:bg-welock-accentHover disabled:opacity-70 md:flex"
          >
            {connecting ? <Loader2 size={13} className="animate-spin" /> : null}
            {connected ? "Open App" : "Launch App"}
            {!connecting && <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />}
          </button>

          {/* mobile hamburger */}
          <button
            data-testid="nav-hamburger"
            onClick={() => setOpen((o) => !o)}
            className="flex h-9 w-9 items-center justify-center border border-welock-border text-welock-textPrimary md:hidden"
            aria-label="Menu"
          >
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>

        {/* mobile menu */}
        {open && (
          <div className="border-t border-welock-border md:hidden" data-testid="mobile-menu">
            {NAV_LINKS.map((l) => (
              <a
                key={l.id}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.id}`}
                className="block border-b border-welock-border px-5 py-3.5 font-mono text-xs uppercase tracking-widest text-welock-textSecondary"
              >
                {l.label}
              </a>
            ))}
            <button
              data-testid="mobile-launch-app"
              onClick={() => { setOpen(false); onLaunch(); }}
              className="flex w-full items-center justify-center gap-2 bg-welock-accent px-5 py-3.5 font-mono text-xs font-medium text-welock-bg"
            >
              {connected ? "Open App" : "Launch App"} <ArrowRight size={13} />
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

function Hero({ onLaunch, connecting }) {
  return (
    <section className="relative overflow-hidden border-b border-welock-border">
      <div className="absolute inset-0 hairline-grid opacity-30" />
      <div className="relative mx-auto flex max-w-4xl flex-col items-center px-6 pt-36 text-center md:pt-44">
        <Eyebrow>welock defi protocol</Eyebrow>
        <h1 className="mt-6 font-heading text-4xl font-normal leading-[1.05] tracking-tight text-welock-textPrimary sm:text-5xl lg:text-6xl">
          Immutable token and liquidity security on Robinhood Chain
        </h1>
        <p className="mx-auto mt-6 max-w-xl text-base font-light leading-relaxed text-welock-textSecondary sm:text-lg">
          Non custodial token locking, LP position vesting, multisender automation
          and high throughput developer API services. Built for teams that lock to
          earn trust.
        </p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <button
            data-testid="hero-launch-app"
            disabled={connecting}
            onClick={onLaunch}
            className="group flex items-center gap-2 bg-welock-accent px-6 py-3.5 font-mono text-sm font-medium text-welock-bg transition-all hover:bg-welock-accentHover disabled:opacity-70"
          >
            {connecting ? <><Loader2 size={16} className="animate-spin" /> Connecting</> : <>Launch App <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>}
          </button>
          <a
            href="#products"
            data-testid="hero-explore"
            className="border border-welock-border px-6 py-3.5 font-mono text-sm text-welock-textSecondary transition-colors hover:border-welock-hairline hover:text-welock-textPrimary"
          >
            Explore products
          </a>
        </div>
        <div className="mt-8">
          <RobinhoodMark size={18} withLabel />
        </div>
      </div>

      {/* peeking dashboard preview */}
      <div className="relative mx-auto mt-16 max-w-5xl px-6">
        <div className="translate-y-2">
          <DashboardPreview />
        </div>
      </div>
    </section>
  );
}

function WhatWeDo() {
  const lines = [
    { scene: "Lock", k: "Lock", v: "Time based, verifiable, non custodial." },
    { scene: "Vest", k: "Vest", v: "Cliff and linear schedules for stakeholders." },
    { scene: "Distribute", k: "Distribute", v: "Batch payouts and airdrops at scale." },
    { scene: "Automate", k: "Automate", v: "API and webhooks for agents and bots." },
  ];
  return (
    <section className="border-b border-welock-border">
      <div className="mx-auto max-w-7xl px-6 py-24 md:px-10">
        <Eyebrow>what welock does</Eyebrow>
        <h2 className="mt-5 max-w-3xl font-heading text-3xl font-normal leading-tight tracking-tight text-welock-textPrimary sm:text-4xl">
          One protocol for locking, vesting and distributing value with proof.
        </h2>
        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {lines.map((l) => {
            const Scene = SCENES[l.scene];
            return (
              <div key={l.k} className="group border border-welock-border bg-welock-surface transition-colors hover:border-welock-hairline">
                <div className="border-b border-welock-border bg-welock-bg/40">
                  <Scene />
                </div>
                <div className="p-6">
                  <div className="font-heading text-xl text-welock-accent">{l.k}</div>
                  <p className="mt-2 text-sm font-light leading-relaxed text-welock-textSecondary">{l.v}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Products({ onLaunch }) {
  return (
    <section id="products" className="border-b border-welock-border">
      <div className="mx-auto max-w-7xl px-6 py-24 md:px-10">
        <Eyebrow>the products</Eyebrow>
        <h2 className="mt-5 font-heading text-3xl font-normal tracking-tight text-welock-textPrimary sm:text-4xl">
          Six tools, one interlock.
        </h2>
        <div className="mt-14 grid auto-rows-[minmax(200px,auto)] grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {PRODUCTS.map((p) => (
            <button
              key={p.id}
              data-testid={`product-${p.id}`}
              onClick={onLaunch}
              className={`group relative flex flex-col justify-between overflow-hidden border border-welock-border bg-welock-surface p-8 text-left transition-colors hover:border-welock-hairline hover:bg-welock-surfaceHover ${p.span || ""}`}
            >
              {p.feature && (
                <div className="pointer-events-none absolute -right-6 -top-6 opacity-[0.04] transition-opacity group-hover:opacity-[0.08]">
                  <WelockMark size={200} />
                </div>
              )}
              <div className="relative flex items-start justify-between">
                <span className="flex h-11 w-11 items-center justify-center border border-welock-border text-welock-textSecondary transition-colors group-hover:border-welock-accent/50 group-hover:text-welock-accent">
                  <p.icon size={18} />
                </span>
                <ArrowUpRight size={16} className="text-welock-textMuted opacity-0 transition-all group-hover:opacity-100 group-hover:text-welock-accent" />
              </div>
              <div className="relative">
                <h3 className={`font-heading text-welock-textPrimary ${p.feature ? "text-2xl" : "text-lg"}`}>{p.title}</h3>
                <p className={`mt-2 font-light leading-relaxed text-welock-textSecondary ${p.feature ? "max-w-md text-base" : "text-sm"}`}>{p.desc}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function Stats() {
  const [stats, setStats] = useState(null);
  useEffect(() => { getStats().then(setStats).catch(() => {}); }, []);
  const items = [
    { label: "Total value locked", value: stats ? fmtUsd(stats.tvl) : "--" },
    { label: "Active locks", value: stats ? fmtNum(stats.active_locks) : "--" },
    { label: "Wallets secured", value: stats ? fmtNum(stats.wallets) : "--" },
    { label: "Supported chain", value: "Robinhood Chain" },
  ];
  return (
    <section id="stats" className="relative border-b border-welock-border">
      <div className="absolute inset-0 hairline-grid opacity-20" />
      <div className="relative mx-auto max-w-7xl px-6 py-24 md:px-10">
        <Eyebrow>live protocol metrics</Eyebrow>
        <div className="mt-12 grid gap-px border border-welock-border bg-welock-border sm:grid-cols-2 lg:grid-cols-4">
          {items.map((it) => (
            <div key={it.label} className="bg-welock-bg p-8" data-testid={`stat-${it.label.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="font-heading text-2xl font-medium tracking-tight text-welock-textPrimary lg:text-3xl">
                {it.value}
              </div>
              <div className="mt-3 font-mono text-[11px] uppercase tracking-widest text-welock-textMuted">
                {it.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function NetworkCallout() {
  return (
    <section id="network" className="border-b border-welock-border">
      <div className="mx-auto grid max-w-7xl gap-12 px-6 py-24 md:grid-cols-2 md:items-center md:px-10">
        <div>
          <Eyebrow>the network</Eyebrow>
          <h2 className="mt-5 font-heading text-3xl font-normal leading-tight tracking-tight text-welock-textPrimary sm:text-4xl">
            Built natively for Robinhood Chain
          </h2>
          <p className="mt-6 max-w-lg text-base font-light leading-relaxed text-welock-textSecondary">
            Seamless EVM compatibility, ultra low fees and direct integration with
            Robinhood ecosystem assets and NFT liquidity positions. One target
            network, zero bridge risk.
          </p>
          <div className="mt-8">
            <RobinhoodMark size={22} withLabel />
          </div>
        </div>
        <div className="flex items-center justify-center border border-welock-border bg-welock-surface p-16">
          <WelockMark size={120} />
        </div>
      </div>
    </section>
  );
}

function CTA({ onLaunch, connecting }) {
  return (
    <section className="relative">
      <div className="mx-auto max-w-7xl px-6 py-28 text-center md:px-10">
        <Eyebrow>aftermarket access</Eyebrow>
        <h2 className="mx-auto mt-5 max-w-2xl font-heading text-3xl font-normal tracking-tight text-welock-textPrimary sm:text-4xl">
          Access the aftermarket of Robinhood Chain.
        </h2>
        <button
          data-testid="cta-launch-app"
          disabled={connecting}
          onClick={onLaunch}
          className="group mt-10 inline-flex items-center gap-2 bg-welock-accent px-8 py-4 font-mono text-sm font-medium text-welock-bg transition-all hover:bg-welock-accentHover disabled:opacity-70"
        >
          {connecting ? <><Loader2 size={16} className="animate-spin" /> Connecting</> : <>Launch App <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>}
        </button>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-welock-border">
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-10 md:flex-row md:items-center md:justify-between md:px-10">
        <WelockLogo />
        <div className="flex items-center gap-8 font-mono text-xs text-welock-textMuted">
          <a href="#products" className="hover:text-welock-textPrimary">Products</a>
          <a href="#stats" className="hover:text-welock-textPrimary">Metrics</a>
          <a href="#network" className="hover:text-welock-textPrimary">Network</a>
          <a href="/docs" className="hover:text-welock-textPrimary">Docs</a>
          <RobinhoodMark size={16} />
        </div>
        <span className="font-mono text-[11px] text-welock-textMuted">
          welock protocol. Non custodial. 2026.
        </span>
      </div>
    </footer>
  );
}

export default function Landing() {
  const { connected, connect } = useWallet();
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);

  const onLaunch = () => {
    if (connected) {
      navigate("/app");
    } else {
      setPending(true);
      connect();
      setTimeout(() => setPending(false), 2500);
    }
  };

  useEffect(() => {
    if (connected && pending) {
      setPending(false);
      navigate("/app");
    }
  }, [connected, pending, navigate]);

  return (
    <div className="min-h-screen bg-welock-bg">
      <Nav onLaunch={onLaunch} connecting={pending} />
      <Hero onLaunch={onLaunch} connecting={pending} />
      <WhatWeDo />
      <Products onLaunch={onLaunch} />
      <Stats />
      <NetworkCallout />
      <CTA onLaunch={onLaunch} connecting={pending} />
      <Footer />
    </div>
  );
}
